import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants/endpoint';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetOrdersParams, TypeGetOrdersResponse, TypeOrders } from '~/types/order.types';

const useGetOrders = () => {
  const { runAsync } = useAsyncHandler();

  const getOrders = useAPI<TypeGetOrdersParams, TypeGetOrdersResponse>({
    endpoint: API_ENDPOINT.ORDER.GET_ORDERS,
    method: 'get',
  });

  const requestOrderList = async (
    params: TypeGetOrdersParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetOrdersResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getOrders({ params }), {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => {
        onError?.(error);
      },
    });
  };

  return { requestOrderList };
};

export default useGetOrders;

const ORDER_RECEIVER_NAMES = ['김컬리', '이물류', '박배송', '최반품', '정교환'];
const ORDER_OUTBOUND_STATUSES: TypeOrders['outbound']['outboundStatus'][] = [
  { code: 'COMPLETED', name: '완료' },
  { code: 'COMPLETED', name: '완료' },
  { code: 'PRODUCING', name: '진행중' },
  { code: 'READY', name: '대기중' },
  { code: 'CANCELED', name: '취소' },
];

/** 반품·교환 신청 1단계 목록도 같은 엔드포인트를 쓴다. 1번 주문의 출고요청번호가 상세 mock 과 이어진다. */
const makeOrder = (index: number): TypeOrders => ({
  order: {
    orderCode: `OR2026051800${index}`,
    clientOrderCode: `CL-20260518-000${index}`,
    paymentAt: `2026-05-18 14:3${index}:00`,
    orderStoreName: '스마트스토어',
    orderItems:
      index === 1
        ? [
            { itemName: '[컬리] 유기농 바나나 1kg', itemId: 'G00000001' },
            { itemName: '[컬리] 무항생제 특란 20구', itemId: 'G00000002' },
            { itemName: '[컬리] 제주 감귤 2kg', itemId: 'G00000003' },
          ]
        : [{ itemName: `[컬리] 샘플 상품 ${index}`, itemId: `G0000001${index}` }],
  },
  orderer: { id: `e2ebuyer0${index}` },
  receiver: { name: ORDER_RECEIVER_NAMES[index - 1] ?? '김컬리' },
  outbound: {
    outboundCode: `OB20260518000${index}`,
    outboundStatus: ORDER_OUTBOUND_STATUSES[index - 1] ?? { code: 'COMPLETED', name: '완료' },
    clusterCenter: { code: 'CC02', name: '김포' },
  },
  delivery: {
    operation: { code: 'DAWN', name: '새벽배송' },
    deliveryDate: '2026-05-20',
    courier: { code: 'KURLY', name: '컬리' },
  },
});

const buildOrdersResponse = (count: number): TypeGetOrdersResponse => ({
  data: Array.from({ length: count }, (_, i) => makeOrder(i + 1)),
  pagination: { listView: 50, totalCount: count, cursor: 0 },
});

registerMockAPI<TypeGetOrdersResponse>({
  method: 'get',
  endpoint: '/api-oms/orders',
  label: ['출고주문/출고주문조회/목록', '반품교환/반품신청등록/주문선택', '반품교환/교환신청등록/주문선택'],
  scenarios: [
    { name: '정상 응답 — 5건', response: buildOrdersResponse(5) },
    { name: '검색 결과 0건', response: buildOrdersResponse(0) },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '출고 주문을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
